import axios from 'axios';
import { api } from '../../axios-config';

export interface PayerInfo {
  first_name: string | null;
  last_name: string | null;
}

export interface Identification {
  type: string;
  number: string;
}

export interface Item {
  category_id: string;
  description: string;
  id: string;
  quantity: string;
  title: string;
  unit_price: string; // O JSON mostra como string (ex: "11") ou número em outros campos
  _id: string;
}

export interface AdditionalInfo {
  payer: PayerInfo;
  tracking_id: string;
  items: Item[];
}

export interface Card {
  cardholder: {
    identification: Identification;
    name: string;
  };
  first_six_digits: string;
  last_four_digits: string;
  expiration_month: number;
  expiration_year: number;
  date_created: string;
  date_last_updated: string;
}

export interface TransactionDetails {
  installment_amount: number;
  total_paid_amount: number;
  net_received_amount: number;
  overpaid_amount: number;
}

export interface EventInfo {
  creatorId: string;
  name: string;
  typeEvent: string;
  startEvent: string;
  endEvent: string;
  status: string;
  published: boolean;
  createdAt: string;
  organizerId: string;
  isRankOpen: boolean;
  shortDescription: string;
  id: string;
}

export interface Athlete {
  name: string;
  email: string;
  confirmEmail: string;
  phone: string;
  gender: 'MASCULINO' | 'FEMININO';
  nationality: string;
  documentType: string;
  cpf: string;
  birthdate: string;
  tshirt: string;
  leader: boolean;
  _id: string;
}

export interface Inscription {
  eventId: string;
  eventCategoryId: string;
  eventLotId: string;
  paymentStatus: string;
  dateStartInscription: string;
  teamName: string;
  boxName: string;
  paymentMPId: string[];
  athletes: Athlete[];
  createdAt: string;
  paymentId: string;
  id: string;
}

export interface FeeDetail {
  type: 'mercadopago_fee' | 'application_fee' | string;
  amount: number;
  _id: string;
}

export interface Pagamento {
  additionalInfo: AdditionalInfo;
  payer: {
    identification: Identification;
    phone: {
      area_code: string | null;
      number: string | null;
      extension: string | null;
    };
    id: string;
    type: string | null;
    email: string;
    first_name: string | null;
    last_name: string | null;
    entity_type: string | null;
  };
  card: Card;
  transactionDetails: TransactionDetails;
  eventId: EventInfo;
  eventLotId: string;
  eventCategoryId: string;
  inscriptionId: Inscription;
  paymentMPId: string;
  paymentMethodId: string;
  paymentTypeId: string;
  installments: number;
  status: 'approved' | 'pending' | 'rejected' | string;
  currencyId: 'BRL' | string;
  description: string | null;
  collectorId: number;
  externalReference: string;
  transactionAmount: number;
  couponAmount: number;
  taxesAmount: number;
  feeDetails: FeeDetail[];
  authorizationCode: string;
  createdAt: string;
  id: string;
}

// O objeto principal é um array de pagamentos
export type PagamentosResponse = Pagamento[];

const CALCULAR_PAGAMENTO = `/payment/calcular-pagamento/categoryId`;
const LOTE_ID = `loteId`;
const CUPOM_ID = `cupomId`;

export type CreatePreference = {
  categoryId: string | undefined;
};

export async function createPreference(data: CreatePreference) {
  const response = await api.post(`/payment/create-preference`, data);

  if (response.status !== 201) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export type AddProductInCart = {
  athleteId?: string;
  teamId?: string;
  categoryId: string;
};

export async function addProductInCart(data: AddProductInCart) {
  const response = await api.post(`/shopping-cart`, data);
  if (response.status !== 201) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export async function deleteProductInCart(productId: string) {
  const response = await api.delete(`/shopping-cart/${productId}`);
  if (response.status !== 200) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export type PaymentMethodFetch = {
  teamId?: string;
  categoryId: string;
  paymentMethodId: string;
};

export async function paymentMethodFetch(data: PaymentMethodFetch) {
  const response = await api.post(`/payment/create-payment`, data);

  if (response.status !== 201) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export type PaymentCreditCard = {
  categoryId: string;
  cardId: string;
  dueDate: string;
  securityCode: string;
  cardholderName: string;
  cpfOrCnpj: string;
  installments: number;
  paymentMethodId: string;
  issuerId: number;
};

export async function paymentCreditCard(data: PaymentCreditCard) {
  const response = await api.post(`/payment/create-preference-creditcard`, data);
  if (response.status !== 201) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export async function paymentCreditCardTest(
  categoryId: string,
  token: string,
  issuerId: number,
  paymentMethodId: string,
  installments: number,
  email: string,
  identificationType: string,
  identificationNumber: string
) {
  const body = {
    categoryId,
    token,
    issuerId,
    paymentMethodId,
    installments,
    description: 'teste',
    payer: {
      email,
      identification: {
        type: identificationType,
        number: identificationNumber,
      },
    },
  };

  const response = await api.post(`/payment/create-preference-creditcard`, body);

  if (response.status !== 201) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export type Product = {
  categoryId: string;
  description: string;
  image?: string;
  name: string;
  price: number;
  quantity: number;
  teamId: string;
  _id: string;
};

export type GetAllShoppingCartResponse = {
  createdAt: string;
  id: string;
  userId: string;
  products: Product[];
};

export async function getAllShoppingCart(): Promise<GetAllShoppingCartResponse> {
  const response = await api.get(`/shopping-cart`);

  if (response.status !== 200) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export type GetPaymentStatus = {
  externalReference: string;
};

export async function getPaymentStatus({ externalReference }: GetPaymentStatus) {
  const response = await api.get(`/payment/status/${externalReference}`);

  if (response.status !== 200) {
    throw new Error('Erro no registro');
  }
  return response.data;
}

export async function listPayments() {
  const response = await api.get(`/payment/`);
  console.log('🚀 ~ listPayments ~ response:', response);

  if (response.status !== 200) {
    throw new Error('Erro ao listar pagamentos');
  }
  return response.data;
}

export type SendPaymentStatus = {
  payment: string;
  status: string;
  merchantOrder: string;
};

export async function sendPaymentStatus(data: SendPaymentStatus) {
  // const response = await api.post(`/payment/create-preference`, data);

  // if (response.status !== 201) {
  //   throw new Error('Erro no registro');
  // }
  return [];
}

export async function sendPayment(dataPayment: any) {
  const { data } = await api.post(`/payment/creditcard`, dataPayment);
  return data;
}

export type PixPaymentData = {
  description: string;
  paymentMethodId: string;
  eventId: string;
  inscriptionId: string;
};

export async function postPixPayment(data: PixPaymentData) {
  const response = await api.post(`/payment/pix`, data);
  if (response.status !== 201) {
    throw new Error('Erro ao cadastrar ticket');
  }
  return response.data;
}

export type CardData = {
  card_number: string;
  cardholder: {
    name: string;
    identification: {
      type: string;
      number: string;
    };
  };
  security_code: string;
  expiration_month: string;
  expiration_year: string;
};

export async function postCardToken(publicKey: string, cardData: CardData) {
  const response = await axios.post(
    `https://api.mercadopago.com/v1/card_tokens?public_key=${publicKey}`,
    cardData
  );
  if (response.status === 400) {
    throw new Error('Erro na construção do Card Token');
  }
  return response.data;
}

export async function getClientMercadoPago(email: string, public_key: string) {
  const response = await axios.get(
    `https://api.mercadopago.com/v1/customers/search?email=${email}`,
    {
      headers: {
        Authorization: `Bearer ${public_key}`,
      },
    }
  );
  if (response.status !== 200) {
    throw new Error('Erro ao acessar Cliente Mercado Pago');
  }
  return response.data;
}

export type CalcularValorInscricaoResponse = {
  teamSize: number;
  valorInscricao: number;
  taxaPlataforma: number;
  cupomDesconto: number;
  tipoTaxa: string;
  valorTotal: number;
  quantTaxa: number;
};

export async function getCalcularPreco(categoryId: string, loteId: string, cupomId?: string) {
  const resp = await api.get<CalcularValorInscricaoResponse>(
    `${CALCULAR_PAGAMENTO}/${categoryId}/${LOTE_ID}/${loteId}/${CUPOM_ID}/${cupomId}`
  );
  return resp.data;
}
